<template>
  <form>
    <FormHeader :title="title" @cancel="onCancel" @submit="onSubmit" />
    <div class="space-y-12 mt-5">
      <div class="border-b border-gray-900/10 pb-12">
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Détails du compromis de vente") }}</h3>
        <FormDateField v-model="signatureDate" field-path="attributes.signatureDate"
          :field-label="$gettext('Date de signature')" :field-attr="signatureDateAttrs" :errors="errors" />
        <FormMoneyField v-model="finalPrice" field-path="attributes.finalPrice" :field-label="$gettext('Prix final')"
          :field-attrs="finalPriceAttrs" :errors="errors" />
        <FormMoneyField v-model="deposit" field-path="attributes.deposit"
          :field-label="$gettext('Montant de l’acompte')" :field-attrs="depositAttrs" :errors="errors" />
        <FormMoneyField v-model="balance" field-path="attributes.balance" :field-label="$gettext('Solde')"
          :field-attrs="balanceAttrs" :errors="errors" :enabled="false" />
        <FormTextField v-model="buyerAccountNumber" field-path="attributes.buyerAccountNumber"
          :field-label="$gettext('Compte de l\'acheteur')" :field-attrs="buyerAccountNumberAttrs" :errors="errors" />
        <FormSelectField v-model="buyerAccountCountryId" field-path="relationships.buyerAccountCountry.data.id"
          :field-label="$gettext('Pays du compte de l\'acheteur')" :field-attrs="buyerAccountCountryIdAttrs"
          :errors="errors" :options="countryOptions" />
        <div class="border-b border-gray-900/10 pb-12">
          <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Documents à importer") }}</h3>
          <FormDocumentsField v-model="purchaseAgreementDocuments" category-name="signedPurchaseAgreement"
            field-path="relationships.purchaseAgreementDocuments.data"
            :field-label="$gettext('Compromis de vente signé')" field-description=""
            :field-attrs="purchaseAgreementDocumentsAttrs" :errors="errors" />
        </div>
      </div>
    </div>
    <FormFooter @cancel="onCancel" @submit="onSubmit" />
  </form>
</template>

<script setup>
import { useForm } from "vee-validate";
import * as yup from "yup";
import { watch, computed } from "vue";
import FormHeader from "@/components/form-fields/FormHeader.vue";
import FormFooter from "@/components/form-fields/FormFooter.vue";
import { useGettext } from "vue3-gettext";
import FormTextField from "@/components/form-fields/FormTextField.vue";
import { useCountryStore } from "@/stores/country";
import FormMoneyField from "../form-fields/FormMoneyField.vue";
import FormDateField from "../form-fields/FormDateField.vue";
import FormSelectField from "../form-fields/FormSelectField.vue";
import FormDocumentsField from "../form-fields/FormDocumentsField.vue";

const { $gettext } = useGettext();
const props = defineProps(["purchaseAgreement", "title"]);
const emits = defineEmits(["submit", "cancel"]);
const countryStore = useCountryStore();

const validationSchema = yup.object({
  attributes: yup.object({
    balance: yup.number().required($gettext("est obligatoire")),
    deposit: yup.number().required($gettext("est obligatoire")),
    finalPrice: yup.number().required($gettext("est obligatoire")),
    buyerAccountNumber: yup.string().required($gettext("est obligatoire")),
    signatureDate: yup.date().required($gettext("est obligatoire"))
  }),
  relationships: yup.object({
    buyerAccountCountry: yup.object({
      data: yup.object({
        id: yup.string().required($gettext("est obligatoire"))
      })
    }),
  })
});

const { errors, handleSubmit, defineField, setValues } = useForm({
  validationSchema: validationSchema,
  initialValues: props.purchaseAgreement
});

const [balance, balanceAttrs] = defineField("attributes.balance");
const [deposit, depositAttrs] = defineField("attributes.deposit");
const [finalPrice, finalPriceAttrs] = defineField("attributes.finalPrice");
const [buyerAccountNumber, buyerAccountNumberAttrs] = defineField("attributes.buyerAccountNumber");
const [buyerAccountCountryId, buyerAccountCountryIdAttrs] = defineField("relationships.buyerAccountCountry.data.id");
const [signatureDate, signatureDateAttrs] = defineField("attributes.signatureDate");
const [purchaseAgreementDocuments, purchaseAgreementDocumentsAttrs] = defineField("relationships.purchaseAgreementDocuments.data");

const countryOptions = computed(() =>
  countryStore.sortedCountries.map((country) => ({
    value: country.id,
    label: country.attributes.name,
  }))
);

const extractCountryCodeFromIBAN = (iban) => {
  if (!iban || iban.length < 2) return null;
  return iban.substring(0, 2).toUpperCase();
};

const updateCountryFromIBAN = (iban) => {
  const countryCode = extractCountryCodeFromIBAN(iban);
  if (!countryCode) return;

  const matchedCountry = countryStore.sortedCountries.find(
    (country) => country.attributes.code === countryCode
  );

  if (matchedCountry) {
    buyerAccountCountryId.value = matchedCountry.id;
  }
};

const onFormSubmit = ((values, actions) => {
  const payload = {
    ...values
  };
  emits("submit", payload, actions);
});

const onSubmit = (() => {
  handleSubmit(onFormSubmit)();
});

const onCancel = (() => {
  emits("cancel");
});

watch([finalPrice, deposit], ([newFinalPrice, newDeposit]) => {
  const computedBalance = (newFinalPrice || 0) - (newDeposit || 0);
  balance.value = computedBalance;
});

watch(buyerAccountNumber, (newAccountNumber) => {
  updateCountryFromIBAN(newAccountNumber);
});

watch(() => props.purchaseAgreement, (newPurchaseAgreement) => {
  setValues(newPurchaseAgreement);
});
</script>
